import { useCallback, useEffect, useState } from "react";
import config from "./config";
import { cachedPost } from "./apiCache";

/**
 * One request for the whole right-hand rail.
 *
 * The rail's widgets — My Points, the earned badges beside it, the inline and
 * popup banners — each used to fetch for themselves: membership-leaderboard,
 * membership-rewards and membership-banners, three round trips per page view
 * for one column of the screen, plus a repeated `funnels` lookup inside each.
 * /membership-rail answers all three in a single request (see
 * MembershipRailController) and resolves the membership once.
 *
 * Every widget on a page MUST build the same body, or cachedPost keys them
 * apart and the merge is undone — so the body is built here, in one place, and
 * the widgets pass the same inputs rather than assembling their own.
 *
 * `parts` is sorted so two callers asking for the same set produce a
 * byte-identical body regardless of the order they list them in.
 */
export function railBody({ token, groupToken, member, placement, parts }) {
  return {
    community_token: token,
    group_token: groupToken,
    community_member_id: member?.community_member_id || undefined,
    member_token: member?.auth_token,
    placement,
    parts: [...parts].sort(),
  };
}

/**
 * Fetch the rail. `parts` narrows what the server builds — a points refresh
 * after a member earns points asks for `points` alone rather than dragging the
 * banners and badges along with it.
 *
 * Returns the whole payload; each widget reads the keys it needs. Sections are
 * independent server-side: one failing part comes back null and names itself in
 * `errors` while the rest still render, so a broken banner cannot blank the
 * points card.
 */
export function useRail({ token, groupToken, member, placement, parts, ready = true, ttl = 30000 }) {
  const [data, setData] = useState(null);
  const memberId = member?.community_member_id;
  const authToken = member?.auth_token;
  const key = [...parts].sort().join(",");

  const doFetch = useCallback(
    (force) => {
      if (!token || !ready) return Promise.resolve();
      return cachedPost(
        `${config.COMMUNITY_API_URL}/membership-rail`,
        railBody({
          token,
          groupToken,
          member: { community_member_id: memberId, auth_token: authToken },
          placement,
          parts: key.split(","),
        }),
        { ttl, force }
      )
        .then((res) => {
          if (res.data?.error === 0) setData(res.data);
        })
        .catch(() => {});
    },
    [token, groupToken, memberId, authToken, placement, key, ready, ttl]
  );

  useEffect(() => {
    doFetch(false);
  }, [doFetch]);

  const refresh = useCallback(() => doFetch(true), [doFetch]);

  return { data, refresh };
}

/**
 * The parts every rail widget asks for, as one shared constant.
 *
 * They must all request the SAME set: cachedPost keys on the body, so a widget
 * asking for a different combination would open a second request rather than
 * share the first. Banners are not in here yet — the banner widgets key their
 * request on `placement`, which would have to be threaded through every
 * useMyPoints call site to keep the bodies identical.
 *
 * `badges`, not `rewards`: the card shows the member's EARNED badges and
 * nothing else, so the rail carries only those. The full badge and reward
 * catalogue is /membership-badge-reward-list, which the Rewards page calls for itself.
 */
export const RAIL_PARTS = ["points", "badges"];
